import { useState, type ReactNode } from "react";
import { checkPolicy, type PolicyReread } from "../../../features/profile/policy";
import { canRequestCode, checkoutUnresolved, screenOf } from "../../../features/guestCheckout/machine";
import type { useGuestCheckout, SubmitContext } from "../../../features/guestCheckout/useGuestCheckout";
import BookingOutcome from "../BookingOutcome";
import {
  AlreadyBookedUnreachable,
  CannotStart,
  GuestUncertain,
  GuestUnavailable,
  HoldEnded,
  IdentityLost,
  RequestLimited,
  RequestProblem,
  SessionNote,
  type HoldView,
} from "./GuestSteps";
import { guestCodeChunk, useChunkBeforeWrite, WithStepChunk } from "../lazySteps";
import { StepSkeleton } from "../StepSkeleton";
import { requestStepFocus } from "../../../components/PageShell";

/**
 * One guest checkout on screen. Everything it shows is `screenOf(state)`, and
 * everything it can do is a callback into `useGuestCheckout`, so the real flow
 * and the local harness render the identical journey.
 *
 * `review` is the entry screen. In the flow that is StepReview, whose primary
 * action calls `checkout.requestCode(email)`; it is passed in rather than
 * built here so this component does not own the flow's routing.
 *
 * `failed` renders the existing booking-failure screens (taken, settings
 * changed, already booked) the flow already has, rather than a second copy.
 */
export function GuestCheckoutView({
  checkout,
  hold,
  submit,
  business,
  when,
  name,
  review,
  failed,
  unauthorized,
  onRecheckTime,
  onChooseAnother,
  onChangeEmailOnDetails,
  onSignIn,
  rereadPolicy,
  clock = Date.now,
}: {
  checkout: ReturnType<typeof useGuestCheckout>;
  hold: HoldView;
  submit: SubmitContext;
  business: string;
  when: string;
  name: string;
  /**
   * The entry screen, given the notice it must carry INSIDE its own column.
   * It used to be prepended outside the review's <main>, where it spanned the
   * viewport with the kit's scale unset (QE A3, 2026-09-22).
   */
  review: (notice: ReactNode) => ReactNode;
  failed: ReactNode;
  unauthorized: ReactNode;
  onRecheckTime: () => void;
  onChooseAnother: () => void;
  /** The unavailable screen's "Change email": back to Details, where the address is edited. */
  onChangeEmailOnDetails: () => void;
  /** The unavailable screen's "Sign in" when guest verification is off; absent when sign-in is not offered. */
  onSignIn?: () => void;
  /** rc.10: read the policy again before Verify sends anything. */
  rereadPolicy: () => Promise<PolicyReread>;
  clock?: () => number;
}) {
  const { state } = checkout;
  const screen = screenOf(state);
  // rc.10: the press is re-reading the policy (busy), and whether the last
  // press stopped because the policy could not be read (the next press is the
  // retry). A change goes back to Review through the checkout itself.
  const [checking, setChecking] = useState(false);
  // A press that returns to Review ("Back", "Start again", "Try again") moves
  // focus to Review's heading, as any user-initiated step change does (c6-3);
  // the pressed button unmounts with its screen (review of 6d53231b, P2).
  const restartToReview = () => {
    requestStepFocus();
    checkout.restart();
  };
  // Every code request here leads to the code step: its chunk first, so a
  // dead chunk sends nothing (rc.12 (a); useChunkBeforeWrite).
  const codeStepReady = useChunkBeforeWrite(guestCodeChunk);
  // Every press below that changes the screen asks for the new screen's
  // heading (c6-3), as Continue does: a guest screen mounted without a press
  // (Back or Forward) keeps focus where it is (rc.13, GuestScreen).
  const requestCode = (email: string) => {
    requestStepFocus();
    void codeStepReady().then((here) => {
      if (here) void checkout.requestCode(email);
    });
  };
  const [unreadable, setUnreadable] = useState(false);
  // No try statement in a component closure (the React Compiler bails out on
  // it): the busy flag clears in the promise's own finally.
  const verify = (code: string) => {
    requestStepFocus();
    setChecking(true);
    void checkout
      .verifyAndBook(code, submit, async () => {
        const check = await checkPolicy(rereadPolicy, state.policy);
        setUnreadable(check === "unreadable");
        return check;
      })
      .finally(() => setChecking(false));
  };

  switch (screen) {
    case "review":
      // A session that could not be read on "Continue" sent nothing; say so
      // above the review rather than leave the tap silent (reviewer P2).
      return <>{review(state.sessionUnavailable ? <SessionNote /> : null)}</>;
    // The code step and change email are the `guest-code` chunk (rc.12 (a)),
    // prefetched when guest Review mounted: a skeleton only while it is
    // really on its way.
    case "code":
    case "submitting":
      return (
        <WithStepChunk chunk={guestCodeChunk} fallback={<StepSkeleton testId="step-chunk-loading" />}>
          {({ CodeStep }) => (
            <CodeStep
              state={state}
              hold={hold}
              clock={clock}
              checking={checking}
              policyUnreadable={unreadable}
              onConfirm={verify}
              // Another action makes the last stop's line stale (review of 1d29dc8b).
              onResend={() => {
                setUnreadable(false);
                void checkout.resend();
              }}
              onChangeEmail={() => {
                setUnreadable(false);
                requestStepFocus();
                checkout.editEmail();
              }}
            />
          )}
        </WithStepChunk>
      );
    case "change-email":
      return (
        <WithStepChunk chunk={guestCodeChunk} fallback={<StepSkeleton testId="step-chunk-loading" />}>
          {({ ChangeEmailStep }) => (
            <ChangeEmailStep
              initial={state.email ?? ""}
              hold={hold}
              clock={clock}
              canSend={canRequestCode(state, clock())}
              sessionUnavailable={state.sessionUnavailable}
              onSend={requestCode}
              onBack={() => {
                requestStepFocus();
                checkout.cancelEdit();
              }}
            />
          )}
        </WithStepChunk>
      );
    case "uncertain": {
      const p = state.phase.name === "uncertain" ? state.phase : null;
      return (
        <GuestUncertain
          business={business}
          checking={p?.checking ?? false}
          checkAlsoLost={p?.checkAlsoLost ?? false}
          canCheck={p?.canCheck ?? false}
          sessionUnavailable={state.sessionUnavailable}
          onCheck={() => {
            requestStepFocus();
            void checkout.check();
          }}
        />
      );
    }
    case "done": {
      const p = state.phase.name === "done" ? state.phase : null;
      if (!p) return null;
      return (
        <BookingOutcome
          outcome={p.outcome}
          kind={p.kind}
          clientEmail={state.email ?? ""}
          guestManage={{ url: p.managementUrl }}
        />
      );
    }
    case "hold-ended":
      return (
        <HoldEnded
          when={when}
          name={name}
          email={state.email ?? ""}
          onRecheck={onRecheckTime}
          onAnother={onChooseAnother}
        />
      );
    case "unavailable":
      return (
        <GuestUnavailable
          business={business}
          reason={state.phase.name === "unavailable" ? state.phase.reason : "guest-booking-unavailable"}
          onAnother={onChooseAnother}
          onChangeEmail={onChangeEmailOnDetails}
          // Not while a booking is unresolved: an identity change would lose
          // its same-identity Check (the sign-in step blocks it too).
          onSignIn={checkoutUnresolved(state) ? undefined : onSignIn}
        />
      );
    case "request-limited": {
      const retryAt = state.phase.name === "request-limited" ? state.phase.retryAt : clock();
      const reason = state.phase.name === "request-limited" ? state.phase.reason : "rate-limited";
      return (
        <RequestLimited
          retryAt={retryAt}
          reason={reason}
          timeZone={hold.timeZone}
          clock={clock}
          onRetry={() => requestCode(state.email ?? "")}
          onBack={restartToReview}
          sessionUnavailable={state.sessionUnavailable}
        />
      );
    }
    case "request-problem":
      return (
        <RequestProblem
          canRetry={canRequestCode(state, clock())}
          onRetry={() => requestCode(state.email ?? "")}
          sessionUnavailable={state.sessionUnavailable}
        />
      );
    case "identity-lost":
      return <IdentityLost onRestart={restartToReview} />;
    case "already-booked-unreachable":
      return <AlreadyBookedUnreachable business={business} />;
    case "cannot-start":
      return <CannotStart onRetry={restartToReview} />;
    case "booking-failed":
      return <>{failed}</>;
    case "unauthorized":
      return <>{unauthorized}</>;
  }
}
